import React, { useEffect, useMemo, useState } from "react";
import { FaSync } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface DueInstallment {
  installmentId: number;
  studentName: string;
  batchName: string;
  courseName: string;
  dueDate: string;
  status: "UNPAID" | "UPCOMING";
  sequenceNumber: number;
  amount: number;
}

const DueInstallments: React.FC = () => {
  const [installments, setInstallments] = useState<DueInstallment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [filterStudent, setFilterStudent] = useState("");
  const [filterCourse, setFilterCourse] = useState("ALL");
  const [filterBatch, setFilterBatch] = useState("ALL");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/installments/due`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch due installments");
      setInstallments(await res.json());
    } catch (e: any) {
      setError(e.message || "Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setCurrentPage(1); }, [filterStudent, filterCourse, filterBatch]);

  const allCourses = useMemo(
    () => Array.from(new Set(installments.map((i) => i.courseName))).sort(),
    [installments]
  );
  const allBatches = useMemo(
    () => Array.from(new Set(installments.map((i) => i.batchName))).sort(),
    [installments]
  );

  const filtered = useMemo(() => {
    return installments.filter((i) => {
      if (filterCourse !== "ALL" && i.courseName !== filterCourse) return false;
      if (filterBatch !== "ALL" && i.batchName !== filterBatch) return false;
      if (filterStudent && !i.studentName.toLowerCase().includes(filterStudent.toLowerCase())) return false;
      return true;
    });
  }, [installments, filterStudent, filterCourse, filterBatch]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  function formatDate(d: string) {
    if (!d) return "-";
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow border p-4 mb-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Student Name</label>
          <input
            type="text"
            value={filterStudent}
            onChange={(e) => setFilterStudent(e.target.value)}
            placeholder="Search..."
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            style={{ fontSize: "16px" }}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Course</label>
          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            style={{ fontSize: "16px" }}
          >
            <option value="ALL">All Courses</option>
            {allCourses.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Batch</label>
          <select
            value={filterBatch}
            onChange={(e) => setFilterBatch(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
            style={{ fontSize: "16px" }}
          >
            <option value="ALL">All Batches</option>
            {allBatches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#009fe3] text-white rounded hover:bg-[#007bbf] transition text-sm disabled:opacity-50"
        >
          <FaSync className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <FaSync className="animate-spin h-10 w-10 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400">
          No due installments found.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-[#009fe3] text-white">
                  <tr>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-4 py-3">Course</th>
                    <th className="px-4 py-3">Batch</th>
                    <th className="px-4 py-3 text-center">Installment #</th>
                    <th className="px-4 py-3">Due Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((i) => (
                    <tr key={i.installmentId} className={`hover:bg-gray-50 ${i.status === "UNPAID" ? "bg-red-50" : ""}`}>
                      <td className="px-4 py-3 font-medium">{i.studentName}</td>
                      <td className="px-4 py-3">{i.courseName}</td>
                      <td className="px-4 py-3 text-gray-600">{i.batchName}</td>
                      <td className="px-4 py-3 text-center">{i.sequenceNumber}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium">
                        <span className={i.status === "UNPAID" ? "text-red-600" : "text-gray-800"}>
                          {formatDate(i.dueDate)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden bg-gray-50 border-t px-4 py-2 text-center">
              <p className="text-xs text-gray-500">← Scroll horizontally to see all columns →</p>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-gray-600">per page</span>
            </div>
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filtered.length)} of {filtered.length}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">First</button>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"><FiChevronLeft /></button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                  let pageNum: number;
                  if (totalPages <= 5) pageNum = idx + 1;
                  else if (currentPage <= 3) pageNum = idx + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + idx;
                  else pageNum = currentPage - 2 + idx;
                  return (
                    <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                        currentPage === pageNum ? "bg-[#009fe3] text-white border-[#009fe3]" : "border-gray-200 hover:bg-gray-50"
                      }`}>{pageNum}</button>
                  );
                })}
              </div>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"><FiChevronRight /></button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Last</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DueInstallments;
